import { TrackedEvents, trackEvent } from 'analytics';
import type { MaterialCreationProcess, MaterialOrigin } from 'api';
import { useTranslation } from 'i18n';
import { useNavigate } from 'react-router-dom';
import { DataCard, ExpandableIconButton, Grid, useBreakpoint } from 'ui';
import { CancelOutlined, CheckCircleOutlined, ImageOutlined, Inventory2Outlined } from 'ui/icons';

import { formatBaleDensity } from '../../utils/formatBaleDensity/formatBaleDensity';
import { formatBaleDimensions } from '../../utils/formatBaleDimensions/formatBaleDimensions';
import {
  formatMaterialCreationProcess,
  formatMaterialOrigin,
} from '../../utils/formatMaterialSpecifications/formatMaterialSpecifications';

const SpecificationAnalysisCheck = ({ value }: { value: boolean }) => {
  const { t } = useTranslation('features');

  return value ? (
    <Grid
      sx={{
        alignItems: 'center',
        gap: 1,
        display: 'flex',
      }}
    >
      <CheckCircleOutlined color="success" sx={{ height: '16px', width: '16px' }} />
      {t('dataCards.materialSpecifications.analysis.completed')}
    </Grid>
  ) : (
    <Grid
      sx={{
        alignItems: 'center',
        gap: 1,
        display: 'flex',
      }}
    >
      <CancelOutlined color="error" sx={{ height: '16px', width: '16px' }} />
      {t('dataCards.materialSpecifications.analysis.notCompleted')}
    </Grid>
  );
};

export const MaterialSpecificationsDataCard = ({
  isLoading,
  storageType,
  materialStrapping,
  height,
  width,
  length,
  weight,
  visualAnalysis,
  radiationAnalysis,
  ferrousAnalysis,
  materialAnalysisNotes,
  materialOrigin,
  materialProcess,
  hasCapsAndLabels,
  isFoodGrade,
  hasMedia,
}: {
  isLoading?: boolean;
  storageType?: string | null;
  materialStrapping?: string | null;
  height?: number | null;
  width?: number | null;
  length?: number | null;
  weight?: number | null;
  visualAnalysis?: boolean | null;
  radiationAnalysis?: boolean | null;
  ferrousAnalysis?: boolean | null;
  materialAnalysisNotes?: string | null;
  materialOrigin?: MaterialOrigin['type'] | null;
  materialProcess?: MaterialCreationProcess | null;
  hasCapsAndLabels?: boolean | null;
  isFoodGrade?: boolean | null;
  hasMedia: boolean;
}) => {
  const { t } = useTranslation('features');
  const navigate = useNavigate();

  const handleClickMedia = () => {
    trackEvent(TrackedEvents.QUALITY_REPORT_CLICK_CATEGORY_IMAGES, {
      category: 'Material Specifications',
    });
    navigate('media?tab=specification');
  };

  const { isMobile } = useBreakpoint();
  const dimensions = formatBaleDimensions({
    height: height,
    length: length,
    width: width,
  });

  const density = formatBaleDensity({
    height: height,
    length: length,
    roundTo: 2,
    weight: weight,
    width: width,
  });

  return (
    <DataCard
      isLoading={isLoading}
      slotProps={{
        header: {
          action: hasMedia ? (
            <ExpandableIconButton
              icon={<ImageOutlined />}
              label={t('dataCards.materialSpecifications.actions.showMedia')}
              onClick={handleClickMedia}
            />
          ) : undefined,
          avatar: <Inventory2Outlined />,
          title: t('dataCards.materialSpecifications.title'),
        },
      }}
    >
      {storageType ? (
        <DataCard.Item title={t('dataCards.materialSpecifications.fields.materialForm')} colSpan={isMobile ? 6 : 2}>
          {storageType}
        </DataCard.Item>
      ) : null}
      {materialStrapping ? (
        <DataCard.Item title={t('dataCards.materialSpecifications.fields.strapping')} colSpan={isMobile ? 6 : 2}>
          {materialStrapping}
        </DataCard.Item>
      ) : null}
      {dimensions ? (
        <DataCard.Item title={t('dataCards.materialSpecifications.fields.baleDimensions')} colSpan={isMobile ? 6 : 2}>
          {dimensions} {t('dataCards.materialSpecifications.units.meters')}
        </DataCard.Item>
      ) : null}
      {density ? (
        <DataCard.Item title={t('dataCards.materialSpecifications.fields.density')} colSpan={isMobile ? 12 : 6}>
          {density}
        </DataCard.Item>
      ) : null}
      {materialOrigin ? (
        <DataCard.Item title={t('dataCards.materialSpecifications.fields.origin')} colSpan={isMobile ? 6 : 2}>
          {formatMaterialOrigin(materialOrigin)}
        </DataCard.Item>
      ) : null}
      {materialProcess ? (
        <DataCard.Item title={t('dataCards.materialSpecifications.fields.materialProcess')} colSpan={isMobile ? 6 : 2}>
          {formatMaterialCreationProcess(materialProcess)}
        </DataCard.Item>
      ) : null}

      {hasCapsAndLabels != null && (
        <DataCard.Item title={t('dataCards.materialSpecifications.fields.capsAndLabels')} colSpan={isMobile ? 6 : 2}>
          {hasCapsAndLabels
            ? t('dataCards.materialSpecifications.values.yes')
            : t('dataCards.materialSpecifications.values.no')}
        </DataCard.Item>
      )}

      {isFoodGrade != null && (
        <DataCard.Item title={t('dataCards.materialSpecifications.fields.foodGrade')} colSpan={isMobile ? 6 : 2}>
          {isFoodGrade
            ? t('dataCards.materialSpecifications.values.yes')
            : t('dataCards.materialSpecifications.values.no')}
        </DataCard.Item>
      )}

      {visualAnalysis != null && (
        <DataCard.Item title={t('dataCards.materialSpecifications.fields.visualCheck')} colSpan={isMobile ? 6 : 2}>
          <div>
            <SpecificationAnalysisCheck value={visualAnalysis} />
          </div>
        </DataCard.Item>
      )}
      {radiationAnalysis != null && (
        <DataCard.Item title={t('dataCards.materialSpecifications.fields.radiationCheck')} colSpan={isMobile ? 6 : 2}>
          <div>
            <SpecificationAnalysisCheck value={radiationAnalysis} />
          </div>
        </DataCard.Item>
      )}
      {ferrousAnalysis != null && (
        <DataCard.Item title={t('dataCards.materialSpecifications.fields.ferrousCheck')} colSpan={isMobile ? 6 : 2}>
          <div>
            <SpecificationAnalysisCheck value={ferrousAnalysis} />
          </div>
        </DataCard.Item>
      )}
      {materialAnalysisNotes ? (
        <DataCard.Item title={t('dataCards.materialSpecifications.fields.materialAnalysisNotes')} colSpan={12}>
          {materialAnalysisNotes}
        </DataCard.Item>
      ) : null}
    </DataCard>
  );
};
